import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Line } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faCode, faQuestionCircle, faLightbulb, faStar } from '@fortawesome/free-solid-svg-icons';

const MarkupMarginComparison = () => {
  const [set1, setSet1] = useState({
    costPrice: '',
    sellingPrice: '',
    markup: '',
    margin: ''
  });
  const [set2, setSet2] = useState({
    costPrice: '',
    sellingPrice: '',
    markup: '',
    margin: ''
  });
  const [comparisonData, setComparisonData] = useState({
    markupDifference: '',
    marginDifference: '',
    moreProfitable: ''
  });
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const calculateSet = (values, setNumber) => {
    try {
      const cp = parseFloat(values.costPrice);
      const sp = parseFloat(values.sellingPrice);

      if (!values.costPrice || !values.sellingPrice) {
        return null;
      }

      if (isNaN(cp) || cp <= 0) {
        throw new Error('Cost price must be a positive number');
      }

      if (isNaN(sp) || sp <= 0) {
        throw new Error('Selling price must be a positive number');
      }

      // Calculate markup percentage
      const markup = ((sp - cp) / cp) * 100;
      // Calculate margin percentage
      const margin = ((sp - cp) / sp) * 100;

      return {
        markup: markup.toFixed(2),
        margin: margin.toFixed(2)
      };
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const handleSet1Change = (e) => {
    const { name, value } = e.target;
    setSet1(prev => ({ ...prev, [name]: value }));
    
    // Update markup and margin when values change
    if (name === 'costPrice' || name === 'sellingPrice') {
      const result = calculateSet({ ...set1, [name]: value }, 1);
      if (result) {
        setSet1(prev => ({ ...prev, markup: result.markup, margin: result.margin }));
      } else {
        // Clear markup and margin if calculation fails
        setSet1(prev => ({ ...prev, markup: '', margin: '' }));
      }
    }
  };

  const handleSet2Change = (e) => {
    const { name, value } = e.target;
    setSet2(prev => ({ ...prev, [name]: value }));
    
    // Update markup and margin when values change
    if (name === 'costPrice' || name === 'sellingPrice') {
      const result = calculateSet({ ...set2, [name]: value }, 2);
      if (result) {
        setSet2(prev => ({ ...prev, markup: result.markup, margin: result.margin }));
      } else {
        // Clear markup and margin if calculation fails
        setSet2(prev => ({ ...prev, markup: '', margin: '' }));
      }
    }
  };

  const calculateComparison = () => {
    setError(''); // Clear any previous errors
    const set1Result = calculateSet(set1, 1);
    const set2Result = calculateSet(set2, 2);

    if (!set1Result || !set2Result) {
      setError('Please enter valid cost and selling prices for both sets');
      return;
    }

    const markupDiff = parseFloat(set2Result.markup) - parseFloat(set1Result.markup);
    const marginDiff = parseFloat(set2Result.margin) - parseFloat(set1Result.margin);

    let moreProfitable = '';
    if (markupDiff > 0) {
      moreProfitable = 'Set 2 has higher markup';
    } else if (markupDiff < 0) {
      moreProfitable = 'Set 1 has higher markup';
    } else {
      moreProfitable = 'Both sets have equal markup';
    }

    setComparisonData({
      markupDifference: markupDiff.toFixed(2),
      marginDifference: marginDiff.toFixed(2),
      moreProfitable
    });

    // Update chart data
    setChartData({
      labels: ['Set 1', 'Set 2'],
      datasets: [
        {
          label: 'Markup Percentage',
          data: [parseFloat(set1Result.markup), parseFloat(set2Result.markup)],
          borderColor: 'rgb(79, 70, 229)',
          tension: 0.1
        },
        {
          label: 'Margin Percentage',
          data: [parseFloat(set1Result.margin), parseFloat(set2Result.margin)],
          borderColor: 'rgb(255, 153, 0)',
          tension: 0.1
        }
      ]
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-blue-100">
      <Helmet>
        <title>Markup and Margin Comparison Calculator</title>
        <meta name="description" content="Compare markup and margin between two sets of cost and selling prices. Analyze profitability and pricing strategies." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <FontAwesomeIcon 
            icon={faChartLine} 
            className="text-4xl text-blue-600 mb-4" 
          />
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Markup and Margin Comparison Calculator</h1>
          <p className="text-xl text-gray-600">Compare pricing strategies between two sets of data</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-xl font-semibold mb-4 text-blue-600">Set 1</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cost Price</label>
                  <input
                    type="number"
                    name="costPrice"
                    value={set1.costPrice}
                    onChange={handleSet1Change}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Selling Price</label>
                  <input
                    type="number"
                    name="sellingPrice"
                    value={set1.sellingPrice}
                    onChange={handleSet1Change}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Markup Percentage</label>
                  <input
                    type="text"
                    value={set1.markup}
                    readOnly
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Margin Percentage</label>
                  <input
                    type="text"
                    value={set1.margin}
                    readOnly
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-50"
                  />
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-4 text-blue-600">Set 2</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cost Price</label>
                  <input
                    type="number"
                    name="costPrice"
                    value={set2.costPrice}
                    onChange={handleSet2Change}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Selling Price</label>
                  <input
                    type="number"
                    name="sellingPrice"
                    value={set2.sellingPrice}
                    onChange={handleSet2Change}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Markup Percentage</label>
                  <input
                    type="text"
                    value={set2.markup}
                    readOnly
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Margin Percentage</label>
                  <input
                    type="text"
                    value={set2.margin}
                    readOnly
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-50"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <button
              onClick={calculateComparison}
              className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Compare Sets
            </button>
          </div>

          {comparisonData.markupDifference && (
            <div className="mt-8 p-6 bg-white rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4 text-blue-600">Comparison Results</h3>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-600">Markup Difference: <span className="font-medium text-blue-600">{comparisonData.markupDifference}%</span></p>
                </div>
                <div>
                  <p className="text-gray-600">Margin Difference: <span className="font-medium text-blue-600">{comparisonData.marginDifference}%</span></p>
                </div>
                <div>
                  <p className="font-semibold text-blue-600">{comparisonData.moreProfitable}</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {chartData && (
            <div className="mt-8">
              <h3 className="text-xl font-semibold mb-4 text-blue-600">Comparison Graph</h3>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <Line
                  data={chartData}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'top',
                      },
                      title: {
                        display: true,
                        text: 'Markup vs Margin Comparison'
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true
                      }
                    }
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="mt-12">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">FAQ</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-gray-700">What is Markup?</h3>
                <p className="text-gray-600 mt-2">Markup is the percentage increase over the cost price that determines the selling price. It represents the profit margin added to the cost price.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">What is Margin?</h3>
                <p className="text-gray-600 mt-2">Margin is the percentage of the selling price that represents profit. It shows what portion of the selling price is profit after covering the cost.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Why compare Markup and Margin?</h3>
                <p className="text-gray-600 mt-2">Comparing markup and margin helps businesses understand their pricing strategies and profitability. Different markup percentages can result in varying margin percentages depending on the cost and selling prices.</p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">Benefits of Using This Calculator</h2>
            <div className="space-y-4">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="text-yellow-500 mr-2" />
                <p className="text-gray-600">Instantly compare pricing strategies between different products or scenarios</p>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faCode} className="text-blue-500 mr-2" />
                <p className="text-gray-600">Visualize the relationship between markup and margin using interactive graphs</p>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faQuestionCircle} className="text-green-500 mr-2" />
                <p className="text-gray-600">Get immediate feedback on which pricing strategy is more profitable</p>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faStar} className="text-purple-500 mr-2" />
                <p className="text-gray-600">Make informed decisions about pricing and profitability</p>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">How to Use This Calculator</h2>
            <ol className="list-decimal pl-6 space-y-4">
              <li className="text-gray-600">Enter the cost price and selling price for Set 1</li>
              <li className="text-gray-600">Enter the cost price and selling price for Set 2</li>
              <li className="text-gray-600">Click the "Compare Sets" button</li>
              <li className="text-gray-600">View the comparison results and graphical representation</li>
              <li className="text-gray-600">Analyze which set has a higher markup and margin</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarkupMarginComparison;
